import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { AlertTriangle, Clock, TrendingUp, AlertCircle, Phone, Mail } from 'lucide-react';
import api from '../../lib/api';

function fmtCLP(n) {
  if (n === null || n === undefined) return '$ 0';
  return Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
}

function ageColor(d) {
  if (d >= 60) return 'bg-red-100 text-red-800 border-red-300';
  if (d >= 45) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-amber-100 text-amber-800 border-amber-300';
}

export default function RCVReportesPage() {
  const [dashboard, setDashboard] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [gapsTotal, setGapsTotal] = useState(0);
  const [gapsMonto, setGapsMonto] = useState(0);
  const [discrepancias, setDiscrepancias] = useState([]);
  const [lag, setLag] = useState([]);
  const [diasMin, setDiasMin] = useState(30);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, g, dx, l] = await Promise.all([
        api.get('/contabilidad/rcv/dashboard-reportes'),
        api.get(`/contabilidad/rcv/reporte-gaps?dias_min=${diasMin}&limit=500`),
        api.get('/contabilidad/rcv/discrepancias?limit=200'),
        api.get('/contabilidad/rcv/lag-proveedores?limit=50'),
      ]);
      setDashboard(d.data || null);
      setGaps(g.data?.items || []);
      setGapsTotal(g.data?.total ?? 0);
      setGapsMonto(g.data?.monto_total ?? 0);
      setDiscrepancias(dx.data?.items || []);
      setLag(l.data?.items || []);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [diasMin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-5 page-enter" data-testid="rcv-reportes-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-indigo-600" />
          Reportes RCV — Gaps, Discrepancias y Lag
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Vista proactiva del estado de DTEs Provisionales: cuáles llevan demasiado sin XML,
          dónde hay diferencias de monto al reconciliar, y qué proveedores tienen mal SLA de envío.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-gap-30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Pendientes XML +30d</p>
              <p className="text-2xl font-bold tabular-nums">{dashboard?.gap_30_dias ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-gap-60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Críticos +60d</p>
              <p className="text-2xl font-bold tabular-nums text-red-600">{dashboard?.gap_60_dias ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-discrep">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <AlertCircle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Discrepancias monto</p>
              <p className="text-2xl font-bold tabular-nums">{dashboard?.discrepancias_monto ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-reconc">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Total reconciliados</p>
              <p className="text-2xl font-bold tabular-nums">{dashboard?.total_reconciliados ?? 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Alert className="border-blue-200 bg-blue-50/50">
        <Mail className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-xs text-blue-900">
          Email automático cada <strong>lunes 8 AM</strong> con resumen de los 3 reportes a los recipients globales.
          Email instantáneo cuando el cron RCV importa &gt;0 provisionales desde el mailbox.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="gaps" className="w-full">
        <TabsList>
          <TabsTrigger value="gaps" data-testid="tab-gaps">
            Gaps sin XML ({gapsTotal})
          </TabsTrigger>
          <TabsTrigger value="discrepancias" data-testid="tab-discrepancias">
            Discrepancias ({discrepancias.length})
          </TabsTrigger>
          <TabsTrigger value="lag" data-testid="tab-lag">
            Lag por proveedor ({lag.length})
          </TabsTrigger>
        </TabsList>

        {/* ─── Tab: GAPS ─── */}
        <TabsContent value="gaps" className="space-y-3">
          <Card>
            <CardHeader className="flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base">Provisionales con +{diasMin} días sin XML</CardTitle>
                {gapsTotal > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {gapsTotal} {gapsTotal === 1 ? 'documento' : 'documentos'} · Monto total: {fmtCLP(gapsMonto)}
                    {gaps.length < gapsTotal && ` · Mostrando primeros ${gaps.length}`}
                  </p>
                )}
              </div>
              <div className="flex gap-1.5">
                {[15, 30, 45, 60, 90].map(n => (
                  <button
                    key={n}
                    onClick={() => setDiasMin(n)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                      diasMin === n
                        ? 'bg-indigo-600 text-white'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                    data-testid={`btn-dias-${n}`}
                  >
                    +{n}d
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {loading && gaps.length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">Cargando…</p>
              ) : gaps.length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  ✅ Todo al día — no hay provisionales con más de {diasMin} días sin XML.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Folio</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>RUT</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead className="text-center">Días sin XML</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gaps.map((g) => (
                      <TableRow key={g.id} data-testid={`gap-row-${g.folio}`}>
                        <TableCell className="font-mono text-xs">{g.folio}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">{g.tipo_dte}</Badge>
                        </TableCell>
                        <TableCell className="text-xs truncate max-w-[260px]" title={g.emisor_razon_social}>
                          {g.emisor_razon_social}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{g.emisor_rut}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{fmtCLP(g.monto_total)}</TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${ageColor(g.dias_sin_xml)}`}>
                            {g.dias_sin_xml}d
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: DISCREPANCIAS ─── */}
        <TabsContent value="discrepancias">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Discrepancias monto RCV vs XML real
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Reconciliaciones donde el XML real difiere del provisional en más de $1.
                Útil para auditoría y detectar posibles errores de declaración.
              </p>
            </CardHeader>
            <CardContent>
              {discrepancias.length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  ✅ Sin discrepancias detectadas en las reconciliaciones recientes.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha reconc.</TableHead>
                      <TableHead>Folio</TableHead>
                      <TableHead>RUT Emisor</TableHead>
                      <TableHead className="text-right">Monto RCV</TableHead>
                      <TableHead className="text-right">Monto XML</TableHead>
                      <TableHead className="text-right">Diferencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discrepancias.map((d) => (
                      <TableRow key={d.id} data-testid={`discrep-row-${d.folio}`}>
                        <TableCell className="text-xs">
                          {(d.fecha || '').slice(0, 10)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{d.folio}</TableCell>
                        <TableCell className="font-mono text-xs">{d.rut_emisor}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{fmtCLP(d.monto_provisional)}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{fmtCLP(d.monto_real)}</TableCell>
                        <TableCell className={`text-right tabular-nums text-xs font-bold ${
                          (d.diff_monto || 0) > 0 ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {(d.diff_monto || 0) > 0 ? '+' : ''}{fmtCLP(d.diff_monto)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: LAG ─── */}
        <TabsContent value="lag">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Lag promedio entre RCV y XML por proveedor
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Proveedores ordenados por peor lag promedio. Útil para llamarlos y exigir
                envío más rápido del XML al correo dte@.
              </p>
            </CardHeader>
            <CardContent>
              {lag.length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  Aún no hay reconciliaciones suficientes para calcular el lag.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>RUT</TableHead>
                      <TableHead className="text-right">Cant. DTEs</TableHead>
                      <TableHead className="text-right">Lag promedio</TableHead>
                      <TableHead className="text-right">Lag máx</TableHead>
                      <TableHead className="text-right">Lag mín</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lag.map((l) => (
                      <TableRow key={l.rut} data-testid={`lag-row-${l.rut}`}>
                        <TableCell className="text-xs truncate max-w-[280px]" title={l.razon_social}>
                          {l.razon_social}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{l.rut}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{l.cantidad_dtes}</TableCell>
                        <TableCell className="text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${
                            l.lag_promedio >= 30 ? 'bg-red-100 text-red-800 border-red-300' :
                            l.lag_promedio >= 14 ? 'bg-amber-100 text-amber-800 border-amber-300' :
                            'bg-emerald-100 text-emerald-800 border-emerald-300'
                          }`}>
                            {l.lag_promedio}d
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{l.lag_max}d</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{l.lag_min}d</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
